import styles from './styles.module.scss'
import clsx from 'clsx'
import { Stop, getColorBasedOnStops } from './getColorBasedOnStops'
import { CustomCSSProperties } from 'moralis-ui'
import { HTMLAttributes } from 'react'

export interface IBatteryChartProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Current value of the battery. Should be between 0 and 1
   */
  value: number
  /**
   * Array of stops to define the color thresholds
   */
  stops?: Array<Stop>
  /**
   * Number of blocks to display
   * @default 10
   */
  numberBlocks?: number
  /**
   * If true, the colors will gradually change based on the value
   * @default false
   */
  gradualStopColors?: boolean
}

export const BatteryChart = ({
  numberBlocks = 10,
  value,
  stops,
  gradualStopColors = false,
  ...props
}: IBatteryChartProps) => {
  // Validate and clamp the input value
  if (typeof value !== 'number' || isNaN(value) || !isFinite(value) || value === undefined) {
    value = 0 // Default to 0 if the value is NaN, Infinite or undefined
  } else {
    value = Math.max(0, Math.min(1, value)) // Clamp value between 0 and 1
  }

  // Calculate the normalized value relative to numberBlocks
  const normalizedValue = value * numberBlocks
  const fullBlocks = Math.floor(normalizedValue) // Full blocks to fill based on normalized value
  const partialFillPercentage = (normalizedValue % 1) * 100 // Calculate the partial fill percentage

  const stopColor = getColorBasedOnStops(value, stops || [], gradualStopColors) // Convert to percentage for compatibility
  const batteryBlocks = []

  for (let i = 0; i < numberBlocks; i++) {
    const isFullBlock = i < fullBlocks
    const isPartialBlock = i === fullBlocks && partialFillPercentage > 0

    // Create an object for CSS properties
    const style: CustomCSSProperties = {}
    if (stopColor) {
      style['--stopColor'] = stopColor // Apply the stopColor if defined
    }
    if (isPartialBlock) {
      style['--fill-percentage'] = `${partialFillPercentage}%` // Apply the fill percentage to partial block
    }

    const classNames = clsx(styles.block, {
      [styles.filledBlock]: isFullBlock,
      [styles.partialFill]: isPartialBlock,
      [styles.stopColor]: stopColor,
    })

    batteryBlocks.push(<div className={classNames} style={style} key={i}></div>)
  }

  return (
    <div className={clsx(styles.chartHolder, props.className)} {...props}>
      <div className={styles.batteryBlocks}>{batteryBlocks}</div>
    </div>
  )
}
